import type {VariantProps} from "class-variance-authority";

import {badgeVariants} from "@/components/ui/badge";
import {DAR_CODE_BADGE_VARIANTS} from "@/src/modules/dars/lib/badge-variants";

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

/** Minutes remaining at or below → red (about to end). */
const CRITICAL_MINUTES = 30;
/** Minutes remaining at or below → yellow (close to end). */
const WARNING_MINUTES = 120;

export type ShiftEndDisplay = {
  variant: BadgeVariant;
  /** e.g. `Your shift ends in 8 hours` */
  text: string;
  /** Suggested client refresh interval (ms) for the countdown label. */
  tickMs: number;
};

function formatRemainingLabel(minutesRemaining: number): string {
  if (minutesRemaining >= 60) {
    const hours = Math.floor(minutesRemaining / 60);
    return hours === 1
      ? "Your shift ends in 1 hour"
      : `Your shift ends in ${hours} hours`;
  }
  if (minutesRemaining === 1) return "Your shift ends in 1 min";
  return `Your shift ends in ${minutesRemaining} min`;
}

function urgencyVariant(minutesRemaining: number): BadgeVariant {
  if (minutesRemaining <= CRITICAL_MINUTES) return DAR_CODE_BADGE_VARIANTS.Red;
  if (minutesRemaining <= WARNING_MINUTES) return DAR_CODE_BADGE_VARIANTS.Yellow;
  return DAR_CODE_BADGE_VARIANTS.Green;
}

function tickIntervalMs(minutesRemaining: number): number {
  if (minutesRemaining <= CRITICAL_MINUTES) return 10_000;
  if (minutesRemaining < 60) return 30_000;
  return 60_000;
}

/** Active-shift chip copy + badge variant from time until `endIso`. */
export function getShiftEndDisplay(endIso: string, nowMs = Date.now()): ShiftEndDisplay {
  const remainingMs = new Date(endIso).getTime() - nowMs;

  if (remainingMs <= 0) {
    return {
      variant: "secondary",
      text: "Shift ended",
      tickMs: 60_000,
    };
  }

  const minutesRemaining = Math.max(1, Math.ceil(remainingMs / 60_000));

  return {
    variant: urgencyVariant(minutesRemaining),
    text: formatRemainingLabel(minutesRemaining),
    tickMs: tickIntervalMs(minutesRemaining),
  };
}
